"use client";
import React, {
  Fragment,
  JSX,
  ReactNode,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import usePaystackPayment from "./use-dukePayment";
import { DukePaymentProps } from "./types";
import clsx from "clsx";

// import { addPaymentToStorefrontCart } from "../../features/cart/cart-slice";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { setCartPayment } from "../../redux/slice/cart-slice";
import { PaystackMobileMoneyProvider, ReasonForPayment } from "_graphql/__generated__/globalTypes";
import { generateRandomAlphanumeric } from "lib/utils";

interface DukePaymentButtonProps extends DukePaymentProps {
  text?: string;
  source?: string,
  className?: string;
  children?: ReactNode;
  provider?: PaystackMobileMoneyProvider;
}

export interface DukePaymentButtonRef {
  execute: (provider?: PaystackMobileMoneyProvider) => void;
  loading: boolean;
}

const DukePaymentButton = forwardRef<
  DukePaymentButtonRef,
  DukePaymentButtonProps
>(
  (
    {
      text,
      // Pass the order source as a prop to the Duke Payment button
      source,
      className,
      children,
      provider ,
      ...others
    },
    ref
  ): JSX.Element => {
    const dispatch = useAppDispatch();
    const { cart } = useAppSelector((state) => state?.cart);
    const { initializePayment, loading, error, checkoutLink } =
      usePaystackPayment();

    const init = useRef({ dispatch });

    function execute(provider_?: PaystackMobileMoneyProvider): void {
      // console.log("----------------------------------------");
      
      // console.log({ provider: provider_ ?? PaystackMobileMoneyProvider.Mtn });
      initializePayment({
        variables: {
          input: {
            email: others.billingEmail ?? "",
            phone: others.phone ?? "",
            amount: others.amount,
            reason_for_payment: ReasonForPayment.order_purchase,
            secret: generateRandomAlphanumeric(10),
            currency: others.currency ?? { name: "Ghana Cedi", code: "GHS" },
            provider: provider_ ?? PaystackMobileMoneyProvider.mtn,
          },
        },
      });
    }

    // expose methods via ref
    useImperativeHandle(ref, () => ({
      execute,
      loading,
    }));

    useEffect(() => {
      if (checkoutLink) {
        // console.log("Checkout Link", checkoutLink);
        const { dispatch } = init.current;

        const hash = checkoutLink.split("#")[1];
        const paymentId = checkoutLink.slice(checkoutLink.lastIndexOf("/") + 1);

        dispatch(
          setCartPayment({
            reference: paymentId,
            method: "pay_online",
            status: "PENDING",
          })
        );

        // dispatch(
        //   setCartPayment({
        //     cart_id: cart?.id as string,
        //     payment: {
        //       amount: others.amount,
        //       invoice: checkoutLink,
        //       payment_id: paymentId,
        //     },
        //   })
        // );

        const baseCheckoutUrl = checkoutLink.split("#")[0];
        // Pass the source variable to the url returned after payment initiation
        const returnUrl = `${window.location.protocol}//${window.location.host}/confirm-payment/${encodeURIComponent(JSON.stringify([source]))}`;
        const finalUrl = `${baseCheckoutUrl}?redirect=${returnUrl}${
          hash ? `#${hash}` : ""
        }`;

        window.location.href = finalUrl;
      }
    }, [checkoutLink, cart, others.amount, error, loading]);

    return (
      <Fragment>
        <button
          disabled={loading}
          className={clsx(
            className,
            "disabled:bg-gray-500 disabled:cursor-not-allowed"
          )}
          onClick={() => execute(provider)}
        >
          {text || children}
        </button>
      </Fragment>
    );
  }
);

DukePaymentButton.displayName = "DukePaymentButton";

export default DukePaymentButton;
